import { Reducer, useCallback, useEffect, useState } from "react";

function getStorageValue<T>(key: string, defaultValue?: T) {
  const saved = localStorage.getItem(key);
  if (!saved) {
    return defaultValue;
  }

  try {
    return JSON.parse(saved);
  } catch (error) {
    console.error(error);
    return defaultValue;
  }
}

// Similar to local storage hook, however doesn't use react state, or effects
// to manage the value.
// Returns a reader and writer function to interact with the local storage.
export const localStorageAccessor = <T>(
  key: string,
  defaultValue: T
): [() => T, (value: T) => void] => {
  const getValue = () => getStorageValue(key, defaultValue);
  const setValue = (value: T) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  return [getValue, setValue];
};

export type LocalStorageHook<T> = [T, React.Dispatch<React.SetStateAction<T>>];

/** Managed local storage hook with a react state */
export const useLocalStorage = <T>(
  key: string,
  defaultValue: T
): LocalStorageHook<T> => {
  const [getLocalValue, setLocalValue] = localStorageAccessor(
    key,
    defaultValue
  );
  const [value, setValue] = useState<T>(getLocalValue);

  useEffect(() => {
    // storing input name
    setLocalValue(value);
  }, [key, value]);

  return [value, setValue];
};

export const usePersistingReducer = <State, Action>({
  reducer,
  persist,
}: {
  reducer: Reducer<State, Action>;
  persist: (state: State) => void;
}) => {
  const persistedReducer = useCallback(
    (state: State, action: Action) => {
      const nextState = reducer(state, action);
      persist(nextState);
      return nextState;
    },
    [reducer, persist]
  );

  return {
    reducer: persistedReducer,
  };
};
